import { useState, useRef, useMemo } from "react";
import { useTheme } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { useSelector } from "react-redux";
import { getWorkForces } from "../../../utils";
import { useModals } from "../../../context/ModalContext";
import { colors, roleColors } from "../../ui/colors";

export type WorkforceFilterType = "all" | "supervisor" | "contractor" | "employee" | "applicant";

export const useWorkforceOverview = (initialFilter: WorkforceFilterType = "all") => {
  const theme = useTheme();
  const [searchValue, setSearchValue] = useState("");
  const [filterType, setFilterType] = useState<WorkforceFilterType>(initialFilter);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [detailsWorkforce, setDetailsWorkforce] = useState<any>(null);
  const detailsTriggerRef = useRef<HTMLButtonElement>(null);

  // @ts-ignore
  const { companies } = useSelector((state: any) => state.userState);
  const company = companies?.[0];

  const { data: workforces = [], isLoading } = useQuery({
    queryKey: ["workforces"],
    queryFn: () => getWorkForces(sessionStorage.getItem("token")),
    enabled: !!company?.id,
  });

  const { closeByKey } = useModals();

  const filteredWorkforces = useMemo(() => {
    let result = workforces;

    if (filterType === "applicant") {
      result = result.filter((w: any) => (w.status || "").toUpperCase() === "APPLICANT");
    } else {
      // Non-applicants should only show ACTIVE (default to ACTIVE if not set)
      result = result.filter((w: any) => {
        const status = (w.status || "ACTIVE").toUpperCase();
        return status === "ACTIVE";
      });

      if (filterType !== "all") {
        result = result.filter((w: any) => (w.workforceRole || w.role) === filterType);
      }
    }

    if (searchValue) {
      const lowerSearch = searchValue.toLowerCase();
      result = result.filter((w: any) => {
        const name = `${w.firstName || ""} ${w.lastName || ""}`.toLowerCase();
        const email = (w.email || "").toLowerCase();
        const phone = (w.phone || "").toLowerCase();
        return name.includes(lowerSearch) || email.includes(lowerSearch) || phone.includes(lowerSearch);
      });
    }

    return result;
  }, [workforces, filterType, searchValue]);

  // Frontend Pagination
  const paginatedWorkforces = useMemo(() => {
    const start = page * pageSize;
    return filteredWorkforces.slice(start, start + pageSize);
  }, [filteredWorkforces, page, pageSize]);

  const formatDate = (dateString: string) => {
    if (!dateString) return "—";
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "2-digit",
    });
  };

  const getWorkforceName = (workforce: any) => {
    return `${workforce.firstName || ""} ${workforce.lastName || ""}`.trim() || "N/A";
  };

  const getWorkforceTypeLabel = (type: string) => {
    if (!type) return "Other";
    return type.charAt(0).toUpperCase() + type.slice(1);
  };

  const getWorkforceTypeColor = (type: string) => {
    // @ts-ignore
    return roleColors[type]?.base || colors.gray[400];
  };

  const handleWorkforceClick = (workforce: any) => {
    setDetailsWorkforce(workforce);
    requestAnimationFrame(() => {
      detailsTriggerRef.current?.click();
    });
  };

  const handleFilterChange = (_: any, newType: WorkforceFilterType) => {
    if (newType !== null) {
      setFilterType(newType);
      setPage(0); // Reset page on filter change
    }
  };

  const activeDetailsWorkforce = useMemo(() => {
    if (!detailsWorkforce?.uid) return detailsWorkforce;
    const found = workforces.find((w: any) => w.uid === detailsWorkforce.uid);
    return found ? { ...detailsWorkforce, ...found } : detailsWorkforce;
  }, [detailsWorkforce, workforces]);

  return {
    theme,
    searchValue,
    setSearchValue,
    filterType,
    page,
    setPage,
    pageSize,
    setPageSize,
    detailsWorkforce: activeDetailsWorkforce,
    detailsTriggerRef,
    loading: isLoading,
    totalItems: filteredWorkforces.length,
    paginatedWorkforces,
    formatDate,
    getWorkforceName,
    getWorkforceTypeLabel,
    getWorkforceTypeColor,
    handleWorkforceClick,
    handleFilterChange,
    closeByKey,
  };
};
